import React, { useState, useContext } from "react";
import {
  Box,
  Typography,
  CardMedia,
  Button,
  Divider,
  Skeleton,
  Snackbar,
  Alert,
} from "@mui/material";
import QRCode from "react-qr-code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

import defaultIcon from "../../../assets/defaultIcon.svg";
import PopUpStampCard from "../popUpStampCard";
import PopUpPointCard from "../popUpPointCard";
import { StoreCard } from "../../../lib/types/detailsTypes";
import { UserContext } from "../../../contexts/user-context";
import { encodeCardQR } from "../utils/encodeCardQR";

interface CardPopupProps {
  card: StoreCard;
}

const FrontPopUp: React.FC<CardPopupProps> = ({ card }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const { userData } = useContext(UserContext) || {};
  const referralCode = userData?.referralCode;

  const shortId = (card as any).shortId || (card as any).shortID || "";
  const referralLink = `https://www.stompcards.com/card/${shortId}?ref=${referralCode}`;

  const qrPayload = encodeCardQR(card);

  const handleCopyReferral = () => {
    if (navigator?.clipboard && referralCode && shortId) {
      navigator.clipboard.writeText(referralLink).then(() => {
        setSnackbarOpen(true);
      });
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: card.cardBackgroundColor,
        boxShadow: "0 0 10px rgba(0,0,0,0.1)",
        borderRadius: "10px",
        overflow: "hidden",
        width: "100%",
        maxWidth: "500px",
        height: "100%",
        padding: 0,
      }}
    >
      {/* Title Image Section */}
      {card.titleImage && (
        <>
          {!imageLoaded && (
            <Skeleton
              variant="rectangular"
              width="100%"
              height="19vh"
              sx={{
                aspectRatio: "2/1",
                borderTopRightRadius: "4px",
                borderTopLeftRadius: "4px",
              }}
            />
          )}

          <Box sx={{ display: imageLoaded ? "block" : "none" }}>
            <CardMedia
              component="img"
              image={card.titleImage}
              alt={`${card.storeName} promotion`}
              onLoad={() => setImageLoaded(true)}
              sx={{
                width: "100%",
                height: "auto",
                aspectRatio: "2/1",
                objectFit: "cover",
                borderTopRightRadius: "4px",
                borderTopLeftRadius: "4px",
              }}
            />
          </Box>
        </>
      )}

      {/* Display Section */}
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        {card.cardType === "pointCard" ? (
          <PopUpPointCard card={card} />
        ) : (
          <PopUpStampCard card={card} defaultIcon={defaultIcon} />
        )}
      </Box>

      {/* Card Details Section */}
      <Box sx={{ px: 2, textAlign: "left" }}>
        <Box sx={{ py: 1, textAlign: "left" }}>
          <Typography variant="h5" sx={{ color: card?.cardTextColor }}>
            <strong>{card.cardName}</strong>
          </Typography>

          <Typography variant="body1" sx={{ color: card?.cardTextColor }}>
            {card.storeName}
          </Typography>
          <Divider sx={{ my: 1, borderColor: card.cardTextColor }} />

          {/* QR + Referral button */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mt: 2,
              px: 1,
            }}
          >
            {/* QRCode */}
            <Box
              sx={{
                width: "100%",
                maxWidth: 200,
                bgcolor: "#ffffff",
                borderRadius: 2,
                p: 2,
                mb: 2,
                boxShadow: "0 1px 3px rgba(0,0,0,.15)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <QRCode
                value={qrPayload}
                size={128}
                fgColor="#000000"
                bgColor="#ffffff"
                style={{ height: "auto", maxWidth: "100%", width: "100%" }}
              />
            </Box>

            {/* Referral Link Button */}
            {card.cardLastScan &&
              card.referralProgramActive &&
              referralCode && (
                <Button
                  variant="outlined"
                  startIcon={<ContentCopyIcon />}
                  onClick={handleCopyReferral}
                  sx={{
                    mt: 1,
                    textTransform: "none",
                    color: card.cardTextColor || "primary.main",
                    borderColor: card.cardTextColor || "primary.main",
                  }}
                >
                  Copy referral link
                </Button>
              )}
          </Box>
        </Box>
      </Box>

      {/* Snackbar Feedback */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          Referral link copied!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FrontPopUp;
